.header {
	position: sticky;

	/* Sit 1px above the top so the band's background covers the sub-pixel sliver that
	   a fractional device-pixel scroll offset can leave between it and the top on
	   high-DPR screens. The extra -1px on the top margin keeps it aligned at rest too. */
	inset-block-start: -1px;
	z-index: var(--z-header);

	/* Full-bleed: cancel the shell's .main padding so the band spans the content
	   pane edge-to-edge and sits flush against the top, reading as one piece with the
	   shell chrome. --gutter equals the band's own inline padding, so the title still
	   lines up with the board columns below. */
	margin-block-start: calc(-1 * var(--spacing-5) - 1px);
	margin-inline: calc(-1 * var(--gutter));

	/* On phones the shell's own top bar is sticky at the top, so drop below it (and
	   1px into it, same sub-pixel reason), and cancel the smaller mobile .main padding. */
	@media (width <= 640px) {
		inset-block-start: calc(2.25rem + var(--spacing-3) * 2);
		margin: calc(-1 * var(--spacing-4) - 1px) calc(-1 * var(--spacing-4)) 0;
	}
}

.band {
	background:
		radial-gradient(
			120% 150% at 0% 0%,
			color-mix(in srgb, var(--colour-brand-primary) 10%, transparent),
			transparent 62%
		),
		var(--colour-background-surface);
	border-block-end: 1px solid var(--colour-border-subtle);
}

.tier1 {
	display: flex;
	gap: var(--spacing-3);
	align-items: flex-start;

	/* Symmetric top/bottom so the collapsed band (tier 1 only) reads balanced. */
	padding: var(--spacing-4) var(--spacing-5);
}

.identity {
	display: flex;
	flex: 1;
	gap: var(--spacing-3);
	align-items: flex-start;
	min-inline-size: 0;
}

.text {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-0-5);
	min-inline-size: 0;
}

/* The project eyebrow: its colour dot + the project name, above the view title. */
.eyebrow {
	display: flex;
	gap: var(--spacing-1-5);
	align-items: center;
	min-inline-size: 0;
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-semibold);
	color: var(--colour-text-secondary);
}

.eyebrow > :last-child {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.dot {
	flex: none;
	inline-size: 0.5rem;
	block-size: 0.5rem;
	background: var(--colour-brand-gradient);
	border-radius: var(--radius-full);
	box-shadow: 0 0 0 3px color-mix(in srgb, var(--colour-brand-primary) 16%, transparent);
}

/* Title + its count pill, on one line below the eyebrow. */
.titleRow {
	display: flex;
	gap: var(--spacing-2);
	align-items: center;
	min-inline-size: 0;
}

.title {
	margin: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	font-size: var(--font-size-lg);
	font-weight: var(--font-weight-bold);
	line-height: 1.15;
	color: var(--colour-text-primary);
	letter-spacing: var(--letter-spacing-tight);
	white-space: nowrap;
}

.action {
	display: flex;
	flex: none;
	align-items: center;
}

/* Tier 2 collapses as you scroll: the grid row goes 1fr -> 0fr (a height collapse)
   while the title above stays put. Driven by a scroll-driven animation below. */
.tier2wrap {
	display: grid;
	grid-template-rows: 1fr;
}

.tier2 {
	min-block-size: 0;
	overflow: hidden;
}

/* The padding sits on an inner element, not on the collapsing grid item: a grid
   item's own padding does not collapse under grid-template-rows: 0fr, so it would
   leave a residual strip below the title once scrolled. */
.tier2inner {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-2);
	padding: 0 var(--spacing-5) var(--spacing-3);
	opacity: 1;
}

@media (width <= 640px) {
	.tier1 {
		padding: var(--spacing-3) var(--spacing-4);
	}

	.title {
		font-size: var(--font-size-md);
	}

	.tier2inner {
		padding: 0 var(--spacing-4) var(--spacing-3);
	}
}

/* Scroll-driven collapse: tie tier 2's height + fade and the band's seam shadow
   directly to the page scroll, so hiding/showing tracks the scroll 1:1 and reverses
   on the way back up. No scroll listener, so there is no threshold to flip-flop and
   no layout-shift feedback loop. Browsers without scroll-driven animations skip this
   block and keep the header expanded. */
@keyframes board-header-collapse {
	to {
		grid-template-rows: 0fr;
	}
}

@keyframes board-header-fade {
	to {
		opacity: 0;
	}
}

@keyframes board-header-seam {
	to {
		box-shadow: 0 8px 20px -16px color-mix(in srgb, var(--colour-text-primary) 70%, transparent);
	}
}

@supports (animation-timeline: scroll()) {
	/* The collapse changes the header's height as you scroll, so turn off the
	   scroller's anchoring: otherwise the browser nudges the scroll offset to keep
	   content in place, which fights the scroll and stops the collapse tracking it
	   1:1. Only applied where the scroll-driven collapse actually runs. */
	:global(html) {
		overflow-anchor: none;
	}

	.tier2wrap {
		animation: board-header-collapse linear both;
		animation-timeline: scroll(root block);
		animation-range: 0 6rem;
	}

	.tier2inner {
		animation: board-header-fade linear both;
		animation-timeline: scroll(root block);
		animation-range: 0 4rem;
	}

	.band {
		animation: board-header-seam linear both;
		animation-timeline: scroll(root block);
		animation-range: 0 6rem;
	}
}
